import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { ApiError, api, type AgentSummary, type Me } from "../lib/api";
import { Badge, Modal, btnGhost, btnPrimary, card, inp } from "../components/ui";
import { authedRoute } from "../main";

// Lista de agentes da conta. O cliente cria por nome (o Agente Global que rege
// é fixo na conta, definido pela Carmillion) até bater max_agents.

const ini = (n: string) => n.split(/[\s—-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function StatusBadge({ a }: { a: AgentSummary }) {
  if (!a.published) return <Badge tone="neutral">Em rascunho</Badge>;
  if (a.has_unpublished_changes) return <Badge tone="warn">Alterações não publicadas</Badge>;
  return <Badge tone="ok">Publicado</Badge>;
}

export function Agents() {
  const { me } = authedRoute.useRouteContext() as { me: Me };
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: agents = [], isLoading } = useQuery<AgentSummary[]>({ queryKey: ["agents"], queryFn: () => api.get("/agents") });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => api.post<AgentSummary>("/agents", { name }),
    onSuccess: (a) => { qc.invalidateQueries({ queryKey: ["agents"] }); setCreating(false); setName(""); nav({ to: "/agentes/$agentId", params: { agentId: a.id } }); },
  });
  const full = agents.length >= me.max_agents;

  return (
    <div style={{ padding: "28px 32px 60px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, letterSpacing: "-.01em" }}>Agentes</h1>
          <p style={{ marginTop: 4, color: "var(--ink-muted)", fontSize: 14 }}>Seus agentes de atendimento. <span style={{ color: "var(--ink-dim)" }}>{agents.length} / {me.max_agents}</span></p>
        </div>
        <button style={{ ...btnPrimary, opacity: full ? 0.5 : 1 }} disabled={full} title={full ? `Sua conta permite até ${me.max_agents} agente(s)` : ""} onClick={() => setCreating(true)}>+ Novo agente</button>
      </div>

      {isLoading && <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>}
      {!isLoading && agents.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ink-muted)", border: "1px dashed var(--line-strong)", borderRadius: 10 }}>
          Nenhum agente ainda.<br /><br />
          <button style={btnPrimary} onClick={() => setCreating(true)}>Criar o primeiro</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {agents.map((a) => (
          <Link key={a.id} to="/agentes/$agentId" params={{ agentId: a.id }} style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 10, textDecoration: "none", color: "inherit", transition: "border-color .12s, transform .12s" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--line-bold)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.transform = ""; }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{ini(a.name)}</div>
                <h2 style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</h2>
              </div>
              <StatusBadge a={a} />
            </div>
            <div style={{ marginTop: "auto", paddingTop: 10, borderTop: "1px solid var(--line-soft)", fontSize: 12, color: "var(--ink-dim)" }}>
              Atualizado em {new Date(a.updated_at).toLocaleDateString("pt-BR")}
            </div>
          </Link>
        ))}
      </div>

      {creating && (
        <Modal title="Novo agente" onClose={() => setCreating(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" }}>Nome do agente</label>
            <input autoFocus style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="Valéria — Auto Center Sul" onKeyDown={(e) => { if (e.key === "Enter" && name.trim().length >= 2 && !create.isPending) create.mutate(); }} />
            <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Você personaliza tudo depois, campo a campo.</span>
          </div>
          {create.error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{(create.error as ApiError).detail ?? "Erro ao criar"}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button style={btnGhost} onClick={() => setCreating(false)}>Cancelar</button>
            <button style={btnPrimary} disabled={create.isPending || name.trim().length < 2} onClick={() => create.mutate()}>{create.isPending ? "Criando…" : "Criar"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
